import {
  useConfirmationDialog,
  usePrinterHooks
} from '@justomx/atlas-components'
import { useSetAtom } from 'jotai'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLoaderData } from 'react-router-dom'

import { Analytics } from 'analytics/analytics.events'
import {
  getContainersProducts,
  getTransferDetail,
  updateOrderStatus
} from 'client/stock-transfers/stock-transfers.client'
import { useQueryParam, useRequiredParams } from 'hooks/params.hook'
import { OrderDetailSchema, type ProductType } from 'types/assembly/model'
import { SCAN_PRODUCT_ERRORS, ZOD_ERROR } from 'utils/constants'
import { toastError } from 'utils/toast'
import { extractWeight, normalizeEanForCapture } from 'utils/upc-helpers'
import { orderContainersAtom, selectedProductAtom } from '../Assemby.state'
import { useGoToMissing } from '../missing/Missing.navigator'
import { useGoToPalletsList } from '../pallets-list/PalletsList.navigator'
import { useGoToPreparation } from '../preparation/Preparation.navigator'
import { useGoToProductForm } from '../product-form/ProductForm.navigator'
import { useGoToScanPrinter } from '../scan-printer/ScanPrinter.navigator'
import { useGoToTransferDetail } from '../transfer-detail/TransferDetail.navigator'

export const useProducts = () => {
  const { transferId, docNum } = useRequiredParams('transferId', 'docNum')
  const containers = useQueryParam('containers')
  const setProduct = useSetAtom(selectedProductAtom)
  const setOrderContainers = useSetAtom(orderContainersAtom)
  const productFormPage = useGoToProductForm()
  const preparationPage = useGoToPreparation()
  const palletsListPage = useGoToPalletsList()
  const transferDetailPage = useGoToTransferDetail()
  const scanPrinterPage = useGoToScanPrinter()
  const missingPage = useGoToMissing()
  const { open, dialogRef } = useConfirmationDialog()
  const { open: openPrinterDialog, dialogRef: printerDialogRef } =
    useConfirmationDialog()
  const {
    open: openMissingsDialog,
    dialogRef: missingsDialogRef,
    value: missingsDialogRefValue
  } = useConfirmationDialog()
  const { isConfigured } = usePrinterHooks()
  const orderRaw = useLoaderData()
  const [products, setProducts] = useState<Record<string, ProductType[]>>({})
  const [filteredProducts, setFilteredProducts] = useState<
    Record<string, ProductType[]>
  >({})
  const [totalContainers, setTotalContainers] = useState(0)
  const [totalProducts, setTotalProducts] = useState(0)
  const [completedSKUs, setCompletedSKUs] = useState(0)
  const [loading, setLoading] = useState(false)
  const [printerReady, setPrinterReady] = useState(false)
  const [warehouse, setWarehouse] = useState('')
  const [pickedFinished, setPickedFinished] = useState(false)

  const formatProductsData = useCallback(
    (fromOrderData: unknown) => {
      const result = OrderDetailSchema.safeParse(fromOrderData)
      if (!result.success) {
        console.info(result.error)
        throw Error(ZOD_ERROR)
      }

      setPickedFinished(!!result.data.pickedFinished)
      const { containers: containerData, toWarehouse } = result.data
      setWarehouse(toWarehouse)
      setTotalContainers(containerData.length)

      let totalSKUsCount = 0
      let completedProducts = 0
      const productsMap: Record<string, ProductType[]> = {}
      const barcodeSet: Record<string, Set<string>> = {
        bulk: new Set(),
        cleaning: new Set(),
        others: new Set()
      }

      for (const { products, totalSKUs } of containerData) {
        totalSKUsCount += totalSKUs

        for (const product of products) {
          if (product.remainingQuantity === 0) {
            completedProducts++
            continue
          }
          if (product.quantity === 0) {
            totalSKUsCount--
            continue
          }

          if (!containers?.includes(product.containerId)) {
            continue
          }

          const zone =
            product.productZone &&
            ['bulk', 'cleaning'].includes(product.productZone)
              ? product.productZone
              : 'others'

          if (!barcodeSet[zone].has(product.barcode)) {
            if (!productsMap[zone]) {
              productsMap[zone] = []
            }
            productsMap[zone].push(product)
            barcodeSet[zone].add(product.barcode)
          }
        }
      }

      setTotalProducts(totalSKUsCount)
      setCompletedSKUs(completedProducts)
      setProducts(productsMap)
      setFilteredProducts(productsMap)
    },
    [containers]
  )

  useEffect(() => {
    isConfigured().then((configured) => {
      setPrinterReady(configured)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    formatProductsData(orderRaw)
  }, [formatProductsData, orderRaw])

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const transfer = await getTransferDetail(transferId)
        const containers = transfer.summarizedContainers.map(
          ({ container }) => container
        )
        setOrderContainers(containers)

        const updatedRawOrder = await getContainersProducts(
          transferId,
          containers.join(',')
        )
        formatProductsData(updatedRawOrder)
      } catch (error) {
        console.warn(error)
      } finally {
        setLoading(false)
      }
    }

    const interval = setInterval(fetchData, 30000)
    return () => {
      clearInterval(interval)
    }
  }, [formatProductsData, setOrderContainers, transferId])

  const allProductsProcessed = useMemo(
    () => completedSKUs === totalProducts,
    [completedSKUs, totalProducts]
  )

  const onSelectFilter = (filterId: string | undefined) => {
    if (filterId) {
      const filteredData = { [filterId]: products[filterId] }
      setFilteredProducts(filteredData)
      return
    }
    setFilteredProducts(products)
  }

  const onGoBack = () => {
    transferDetailPage.go(transferId, docNum)
  }

  const getBarcodeToSearch = (barcode: string): string => {
    let barcodeToSearch = barcode
    const grams = extractWeight(barcode)
    if (grams !== null) {
      barcodeToSearch = normalizeEanForCapture(barcode)
    }
    return barcodeToSearch
  }

  const findProductInOrder = (upc: string): ProductType => {
    let scannedProduct: ProductType | undefined
    Object.values(products).find((prods) =>
      prods.find((product) => {
        if (product.barcode === upc || product.sku === upc) {
          scannedProduct = product
          return true
        }
        return false
      })
    )
    if (!scannedProduct) {
      throw new Error(SCAN_PRODUCT_ERRORS.upcNotFound)
    }
    return scannedProduct
  }

  const onScanProduct = (upc: string) => {
    const barcodeToSearch = getBarcodeToSearch(upc)
    try {
      const scannedProduct = findProductInOrder(barcodeToSearch)
      setProduct(scannedProduct)
      productFormPage.go(transferId, scannedProduct.barcode, docNum)
    } catch (error) {
      if (error.message === SCAN_PRODUCT_ERRORS.upcNotFound) {
        toastError(
          `El producto escaneado no pertenece a la solicitud ${docNum}.`
        )
      }
    }
  }

  const onContinue = async () => {
    if (!allProductsProcessed) {
      toastError(
        'Asegurate de registrar todos los productos antes de continuar.'
      )
      return
    }

    try {
      setLoading(true)
      const result = await updateOrderStatus(transferId, 'readyToWrap')
      Analytics.assembly.updateOrderStatus({
        transferId,
        docNum,
        statusFromBackend: result?.orderStatus || 'error',
        fromScreen: 'products'
      })

      if (printerReady) {
        preparationPage.go(transferId, docNum)
      } else {
        openPrinterDialog()
      }
    } catch (error) {
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  const goToPalletsList = () => {
    palletsListPage.go(transferId)
  }

  const openKeyboard = () => {
    open()
  }

  const goToScanPrinter = () => {
    scanPrinterPage.go(transferId, docNum, true)
  }

  const onClickPrinter = () => {
    if (printerReady) {
      return
    }

    scanPrinterPage.go(transferId, docNum)
  }

  const onSelectProduct = (product: ProductType) => {
    openMissingsDialog(product)
  }

  const goToMissingPage = (missingType: string) => {
    const product = missingsDialogRefValue as ProductType
    setProduct(product)
    missingPage.go(transferId, docNum, missingType, product.barcode)
  }

  return {
    docNum,
    filteredProducts,
    dialogRef,
    totalContainers,
    totalProducts,
    completedSKUs,
    loading,
    allProductsProcessed,
    printerReady,
    printerDialogRef,
    missingsDialogRef,
    warehouse,
    pickedFinished,
    actions: {
      onSelectFilter,
      onGoBack,
      onScanProduct,
      onContinue,
      goToPalletsList,
      openKeyboard,
      goToScanPrinter,
      onSelectProduct,
      goToMissingPage,
      onClickPrinter
    }
  }
}
